import dayjs from 'dayjs'
import { ClipboardList } from 'lucide-react'
import PropTypes from 'prop-types'
import { Link } from 'react-router'

import { Badge } from '@/components/elements/badge'
import { Button } from '@/components/elements/button'
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/elements/card'

const formatDate = (value) => (value ? dayjs(value).format('MMM D, YYYY') : '—')

const STATUS_VARIANTS = {
  'Not Started': 'outline',
  'In Progress': 'warning',
  Issued: 'success',
  Expired: 'destructive',
}

const summarize = (studentCertificates) => {
  const counts = { issued: 0, inProgress: 0, expired: 0 }
  studentCertificates.forEach((sc) => {
    if (sc.status === 'Issued') counts.issued += 1
    else if (sc.status === 'In Progress' || sc.status === 'Not Started') counts.inProgress += 1
    else if (sc.status === 'Expired') counts.expired += 1
  })
  return counts
}

const StudentSection = ({ entityRole, entityId, canManage }) => {
  const studentCertificates = entityRole.studentCertificates || []
  const summary = summarize(studentCertificates)

  return (
    <Card>
      <CardHeader>
        <CardTitle>Student</CardTitle>
        <CardDescription>Course certifications, progress, and compliance.</CardDescription>
        {canManage && (
          <CardAction>
            <Button asChild variant="outline" size="sm">
              <Link to={`/accounts/${entityId}/instructors/students/${entityRole.id}`}>
                <ClipboardList className="mr-1 h-4 w-4" />
                Manage Training
              </Link>
            </Button>
          </CardAction>
        )}
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <div className="grid grid-cols-3 gap-4">
          <div className="flex flex-col rounded-lg border-1 p-3">
            <span className="text-muted-foreground text-xs uppercase">Issued</span>
            <span className="text-2xl font-semibold">{summary.issued}</span>
          </div>
          <div className="flex flex-col rounded-lg border-1 p-3">
            <span className="text-muted-foreground text-xs uppercase">In Progress</span>
            <span className="text-2xl font-semibold">{summary.inProgress}</span>
          </div>
          <div className="flex flex-col rounded-lg border-1 p-3">
            <span className="text-muted-foreground text-xs uppercase">Expired</span>
            <span className="text-2xl font-semibold">{summary.expired}</span>
          </div>
        </div>

        {studentCertificates.length > 0 ? (
          <div className="divide-accent flex flex-col divide-y rounded-lg border-1">
            {studentCertificates.map((sc) => (
              <div
                key={sc.id}
                className="flex flex-row flex-wrap items-center justify-between gap-4 px-4 py-3"
              >
                <div className="flex flex-col">
                  <span className="text-sm font-medium">{sc.certificate?.name || 'Course'}</span>
                  {sc.certificateNumber && (
                    <span className="text-muted-foreground font-mono text-xs">
                      {sc.certificateNumber}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-3">
                  {sc.dateIssued && (
                    <span className="text-muted-foreground text-xs">
                      Issued {formatDate(sc.dateIssued)}
                    </span>
                  )}
                  <Badge variant={STATUS_VARIANTS[sc.status] || 'outline'}>{sc.status}</Badge>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <span className="text-muted-foreground text-sm italic">
            Not enrolled in any courses.
          </span>
        )}
      </CardContent>
    </Card>
  )
}

StudentSection.propTypes = {
  entityRole: PropTypes.shape({
    id: PropTypes.string.isRequired,
    studentCertificates: PropTypes.array,
  }).isRequired,
  entityId: PropTypes.string.isRequired,
  canManage: PropTypes.bool,
}

export default StudentSection
